import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import * as yup from 'yup';
import { ContainerType, SearchType, TicketOrigin, TicketPriority, TicketStatus, UserTypology } from 'helpers/Enums';
import { KeyValuePair } from 'helpers';
import moment from 'moment';
import { LocalizationIds } from 'helpers/Localizations';
import { useIntl } from 'react-intl';
import { applicationContext } from 'helpers/services';
import { SaveResponse } from 'helpers/intefaces';

interface ITicketDetailsContext {
    ticket: ITicket
    isLoading: boolean
    isLoadingTicketDocs: boolean
    isLoadingWatchers: boolean
    isLoadingTicketMessages: boolean
    selectedOrders: ITicketOrder[]
    teamReview: ITeamReview[]
    watcherUsers: IWatcherUser[]
    ticketWatchers: ITicketWatcher[]
    documentContainers: IDocumentContainer[]
    filesToEdit: KeyValuePair<string, string>[]
    teamMembers: ITeamMember[]
    messages: IMessage[]
    isDownloadingDocument: string | null
    showEditDialog: boolean
    reporters: IReporterUser[]
    showCreateReporter: boolean
    assignableUsers: IAssignableUser[]

    updateTicketStatus: (ticketStatus: TicketStatus) => void
    updateTicketPriority: (ticketPriority: TicketPriority) => void
    updateTicketCustomerCanView: (canView: boolean) => void
    updateTicketSubCategory: (subcategory: string) => void
    updateTicketReporterUserName: (userName: string) => void
    updateTicketAssigneeUserName: (userName: string) => void

    searchUsers: (searchText: string) => void
    searchAssignees: (searchText: string) => void
    searchReporters: (searchText: string) => void

    addWatcher: (email: string, receiveNotifications: boolean) => Promise<void>
    updateWatcher: (watcher: ITicketWatcher) => Promise<void>
    removeWatcher: (value: string) => Promise<void>

    addOrder: (value: ITicketOrder) => Promise<void>
    removeOrder: (value: ITicketOrder) => Promise<void>

    handleChangeFilesToEdit: (fileId: string, fileName: string) => void
    handleChangeFileName: (fileId: string, fileName: string) => void
    getTicketDocuments: (ticketNumber: string, showIsDeleted: boolean) => Promise<void>
    downloadDocument: (documentId: string, revisionNr: number) => void
    downloadAttachment: (messageId: string, documentId: string) => void
    deleteDocument: (documentId: string) => void
    renameDocument: (documentId: string) => void
    restoreDocument: (documentId: string) => void
    handleChangeTeamMembers: (value: ITeamMember, index: number | null, action: 'add' | 'edit' | 'delete') => Promise<boolean>
    // updateOrders: (selectedOrder: ITicketOrder, removed?: boolean) => void
    saveMessage: (values: SaveMessage) => Promise<void>
    openEditDialogHandler: () => void
    closeEditDialogHandler: (reloadTicket: boolean) => Promise<void>
    closeReporterDialogHandler: (refresh: boolean) => Promise<void>
    openCreateReporter: () => void
    updateGroups: (groups: string[]) => Promise<void>
}

export interface ITeamMember {
    userName: string
    startDate: moment.Moment | null
    endDate: moment.Moment | null
    otherTickets: IOtherTicket[]
}

interface ITicketWatcher {
    email: string
    receiveNotifications: boolean
    canUpdateNotificationsFlag: boolean
}

interface ITeamReview {
    userName: string
    expectedDays: number
    totalDays: number
    totalWorkedDays: number
}

interface IAssignableUser {
    email: string
    name: string
    groups: string[]
    canWorkOnAllTickets: boolean
}

interface IReporterUser {
    value: string
    label: string
    isExternal: boolean
    newUser: boolean
}

interface IOtherTicket {
    ticketId: string
    ticketNumber: string
    title: string
}

export interface ITicketOrder {
    type: SearchType

    //Ordine cliente
    orderId: number
    orderNumber: string
    orderType: string
    orderDate: moment.Moment
    orderTitle: string

    //Ordine produzione
    workOrderId: number
    workOrderNumber: string
    articleId: string
    articleModel: string
    articleVersion: string
    articleDescription: string
}

export interface IDocumentContainer {
    containerId: string
    containerType: ContainerType
    name: string
    singleFileAllowed: boolean
    isReadOnly: boolean
    documents: IDocument[]
}

export interface IDocument {
    documentId: string
    containerId: string
    documentName: string
    uploadedBy: string
    uploadedOn: moment.Moment
    revisionNr: number
    isDeleted: boolean
}

export interface ITicket {
    ticketId: string
    isReadOnly: boolean
    ticketNumber: string
    title: string
    status: TicketStatus
    ticketSubCategory: string
    ticketSubCategoryId: string
    customerId: number
    customerName: string
    countryCode: string
    countryName: string
    reporterUserName: string
    reporterUser: string
    assigneeUserName: string
    assigneeUser: string
    customerCanView: boolean
    priority: TicketPriority
    orderType: string
    source: TicketOrigin,
    groups: string[]
    createdBy: string,
    createdByFullName: string,
    createdOn: moment.Moment,
    lastUpdatedBy?: string,
    lastUpdatedByFullName?: string,
    lastUpdatedOn?: moment.Moment
}

export interface IMessage {
    messageId: string
    isReadOnly: boolean
    isSystemMessage: boolean
    canCreateTicketFromMessage: boolean
    canBeMoved: boolean
    value: string
    username: string
    userFullName: string
    source: UserTypology
    shareWithCustomer: boolean
    date: moment.Moment
    attachments: IAttachment[]
    originalTicketId?: string
    originalTicketNumber?: string
    originalMessageId?: string
    childrenTickets: KeyValuePair<string, string>[]
}

export interface IAttachment {
    fileId: string
    extension: string
    isImage: boolean
    isVideo: boolean
    fileName: string
    username: string
    date: moment.Moment
}

interface IWatcherUser {
    email: string
    name: string
    isExternal: boolean
}

let attachmentSchema = yup.object({
    fileId: yup.string().default(''),
    isDuplicated: yup.boolean().default(true),
    isGalleryFile: yup.boolean().default(false),
    fileName: yup.string().default('')
});

let mediaWorkOrderSchema = yup.object({
    workOrderId: yup.number().default(0),
    workOrderNumber: yup.string().default('').nullable(),
    articleId: yup.string().default(''),
    articleModel: yup.string().default(''),
    articleVersion: yup.string().default('').nullable(),
    articleDescription: yup.string().default(''),
    orderId: yup.number().default(0),
    orderNumber: yup.string().default('').nullable(),
    orderType: yup.string().default('').nullable(),
    orderTitle: yup.string().default(''),
    orderDate: yup.date().default(moment().toDate()).nullable()
})

export let messageSaveSchema = yup.object({
    messageId: yup.string().default(null).nullable(),
    text: yup.string().default(''),
    shareWithCustomer: yup.boolean().default(false),
    newStatus: yup.string().required().default(TicketStatus.Backlog),
    mediaTitle: yup.string().default(''),
    mediaWorkOrders: yup.array(mediaWorkOrderSchema).default([]),
    attachments: yup.array(attachmentSchema).default([])
});

type SaveMessage = yup.InferType<typeof messageSaveSchema>;
export type MediaWorkOrder = yup.InferType<typeof mediaWorkOrderSchema>;


export let ticketDetailsContext = React.createContext({} as ITicketDetailsContext);

let { Provider } = ticketDetailsContext;

let TicketDetailsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let { id } = useParams<{ id: string }>();
    let { enqueueSnackbar } = useSnackbar();
    let { formatMessage } = useIntl();

    let [isLoading, setLoading] = useState(true);
    let [ticket, setTicket] = useState<ITicket>({} as ITicket);

    let [selectedOrders, setSelectedOrders] = useState<ITicketOrder[]>([]);

    let [isLoadingWatchers, setLoadingWatchers] = useState(false);
    let [ticketWatchers, setTicketWatchers] = useState<ITicketWatcher[]>([]);
    let [watcherUsers, setWatcherUsers] = useState<IWatcherUser[]>([]);
    let [assignableUsers, setAssignableUsers] = useState<IAssignableUser[]>([]);

    let [teamMembers, setTeamMembers] = useState<ITeamMember[]>([]);
    let [teamReview, setTeamReview] = useState<ITeamReview[]>([]);

    let [isLoadingTicketDocs, setLoadingTicketDocs] = useState(false);
    let [filesToEdit, setFileToEdit] = useState<KeyValuePair<string, string>[]>([]);
    let [documentContainers, setDocumentContainers] = useState<IDocumentContainer[]>([]);

    let [isLoadingTicketMessages, setLoadingTicketMessages] = useState(false);
    let [messages, setMessages] = useState<IMessage[]>([]);

    let [isDownloadingDocument, setIsDownloadingDocument] = useState<string | null>(null);

    let [showEditDialog, setShowEditDialog] = useState<boolean>(false);

    let [reporters, setReporters] = useState<IReporterUser[]>([]);

    let [showCreateReporter, setShowCreateReporter] = useState<boolean>(false);

    let appService = useContext(applicationContext);


    useEffect(() => {
        let getAllData = async () => {
            let ticketLoadRes = await getTicket(true, true);
            if (ticketLoadRes === true) {
                getTicketTeamMembers();
                getTicketMessages();
            }
        }
        getAllData();
    }, [id])


    let getTicket = async (setIsLoading: boolean = true, isFirstLoad: boolean = false) => {
        try {
            if (setIsLoading === true) {
                setLoading(true);
            }

            let res = await axios.get('/api/Ticket/GetTicket', { params: { ticketNumber: id } })
            let { item } = res.data;

            setTicket({
                ...item.ticket,
                assigneeUserName: item.ticket.assigneeUserName || '',
                assigneeUser: item.ticket.assigneeUser || ''
            });
            setTicketWatchers(item.ticket.watchers);
            setAssignableUsers(item.assignableUsers);
            setWatcherUsers(item.allTicketWatchers);
            setReporters(item?.reporters || []);
            setSelectedOrders(item?.ticketOrders || []);

            getTicketDocuments(id, false);

            if (setIsLoading === true) {
                setLoading(false);
            }
            return true;
        } catch (err) {
            if (!isFirstLoad) {
                setLoading(false);
            }
            return false;
        }
        finally {
        }
    }

    let getTicketTeamMembers = async () => {
        try {
            let res = await axios.get('/api/Ticket/GetTicketTeamMembers', { params: { ticketNumber: id } })

            let { item } = res.data;
            setTeamMembers(item.teamMembers);
            setTeamReview(item.teamReview);

        } catch (err) { }
    }

    let getTicketMessages = async () => {
        try {
            setLoadingTicketMessages(true);

            let res = await axios.get('/api/TicketMessage/GetTicketMessages', { params: { ticketNumber: id } })

            setMessages(res.data.items);

        } catch (err) { }
        finally {
            setLoadingTicketMessages(false);
        }
    }


    let searchUsers = async (searchText: string) => {
        if (!searchText)
            return;

        try {
            let request = {
                searchText: searchText
            };

            const res = await axios.get('/api/User/GetTicketUsers', { params: request });
            return res.data.items;
        } catch { }
    }

    let searchReporters = async (searchText: string) => {
        if (!searchText)
            return;

        try {

            let request = {
                searchText: searchText,
                ticketId: ticket.ticketId
            };

            const res = await axios.get('/api/User/GetTicketUsers', { params: request });
            return res.data.items;
        } catch { }
    }

    let searchAssignees = async (searchText: string) => {
        if (!searchText)
            return;

        try {
            const res = await axios.get('/api/User/GetTicketAssignableUsers', { params: { searchText } });
            return res.data.items;
        } catch { }
    }


    let addWatcher = async (email: string, receiveNotifications: boolean) => {
        if (ticketWatchers.findIndex(f => f.email === email) !== -1)
            return;

        const newWatchers = [...ticketWatchers, {
            email,
            receiveNotifications,
            canUpdateNotificationsFlag: true
        }];

        await updateTicketWatchers(newWatchers);
        enqueueSnackbar(formatMessage({ id: LocalizationIds.UserAdded }), { variant: "success" });
    }

    const updateWatcher = async (watcher: ITicketWatcher) => {
        let idx = ticketWatchers.findIndex(f => f.email === watcher.email);

        if (idx === -1) {
            enqueueSnackbar(formatMessage({ id: LocalizationIds.UserNotFound }), { variant: "error" });
            return;
        }

        ticketWatchers.splice(idx, 1, watcher);

        setTicketWatchers([...ticketWatchers]);

        try {
            setLoadingWatchers(true);

            const request = {
                ticketId: ticket.ticketId,
                email: watcher.email,
                receiveNotifications: watcher.receiveNotifications
            }

            await axios.put('/api/Ticket/UpdateTicketWatcherNotification', request);

            enqueueSnackbar(formatMessage({ id: LocalizationIds.UserUpdated }), { variant: "success" });

        } catch (err) { }
        finally {
            setLoadingWatchers(false);
        }
    }

    let removeWatcher = async (email: string) => {
        if (window.confirm(formatMessage({ id: LocalizationIds.INFO_RemoveUserFromTicketNotifications })) === false)
            return;

        let idx = ticketWatchers.findIndex(f => f.email === email);
        if (idx === -1)
            return;

        ticketWatchers.splice(idx, 1);

        await updateTicketWatchers([...ticketWatchers]);
        enqueueSnackbar(formatMessage({ id: LocalizationIds.UserRemoved }), { variant: "success" });
    }

    const updateTicketWatchers = async (ticketWatchers: ITicketWatcher[]) => {
        try {
            setLoadingWatchers(true);

            setTicketWatchers([...ticketWatchers]);

            const request = {
                ticketId: ticket.ticketId,
                ticketWatchers
            }

            await axios.put('/api/Ticket/UpdateTicketWatchers', request);

        } catch (err) { }
        finally {
            setLoadingWatchers(false);
        }
    }


    let updateGroups = async (groups: string[]) => {
        let oldGroups = ticket.groups || [];

        try {
            let request = {
                ticketId: ticket.ticketId,
                groups
            };

            await axios.post('/api/Ticket/UpdateGroups', request);
            enqueueSnackbar(formatMessage({ id: LocalizationIds.INFO_TicketSaved }), { variant: "success" });
            getTicket(false);

        } catch (err) {
            setTicket({ ...ticket, groups: oldGroups });
        }
    }

    let getTicketDocuments = async (ticketNumber: string, showIsDeleted: boolean) => {
        try {
            setLoadingTicketDocs(true);

            let res = await axios.get('/api/Ticket/GetTicketDocuments', { params: { ticketNumber, showIsDeleted } });

            let { items } = res.data;
            setDocumentContainers(items);

        } catch (err) { }
        finally {
            setLoadingTicketDocs(false);
        }
    }


    let handleChangeFilesToEdit = (fileId: string, fileName: string) => {
        let idx = filesToEdit.findIndex(x => x.key === fileId);

        if (idx === -1)
            filesToEdit.push(new KeyValuePair(fileId, fileName));
        else
            filesToEdit.splice(idx, 1);

        setFileToEdit([...filesToEdit]);
    }

    let handleChangeFileName = (fileId: string, fileName: string) => {
        let idx = filesToEdit.findIndex(x => x.key === fileId);

        filesToEdit.splice(idx, 1, new KeyValuePair(fileId, fileName));

        setFileToEdit([...filesToEdit]);
    }

    let downloadAttachment = async (messageId: string, documentId: string) => {
        try {
            setIsDownloadingDocument(documentId);
            let res = await axios.get('/api/CustomerTicket/DownloadTicketAttachmentDocument', { params: { messageId: messageId, documentId: documentId } });
            window.location.href = res.data;
            setIsDownloadingDocument(null);
        } catch (err) {
            enqueueSnackbar(formatMessage({ id: LocalizationIds.ERR_FILE_DOWNLOAD }), { variant: "error" });
            setIsDownloadingDocument(null);
        }
    }

    let downloadDocument = async (documentId: string, revisionNr: number) => {
        try {
            setIsDownloadingDocument(documentId);
            let res = await axios.get('/api/Ticket/DownloadDocument', { params: { documentId, revisionNr } })
            window.location.href = res.data;
            setIsDownloadingDocument(null);
        } catch (err) {
            enqueueSnackbar(formatMessage({ id: LocalizationIds.ERR_FILE_DOWNLOAD }), { variant: "error" });
            setIsDownloadingDocument(null);
        }
    }

    let renameDocument = async (documentId: string) => {
        try {
            let request = {
                documentId: documentId,
                newFileName: filesToEdit.find(x => x.key === documentId)?.value
            }

            await axios.post('/api/Ticket/RenameDocument', request)
            //rimuovo dalla lista dei file da editare quello appena salvato
            handleChangeFilesToEdit(documentId, "");
            await getTicketDocuments(ticket.ticketNumber, false);

        } catch (err) {
        }
    }

    let deleteDocument = async (documentId: string) => {
        if (window.confirm(formatMessage({ id: LocalizationIds.INFO_DocumentRemove }))) {
            try {
                await axios.delete(`/api/Ticket/DeleteDocument/${documentId}`)
                await getTicketDocuments(ticket.ticketNumber, false);

            } catch (err) {
            }
        }
    }

    let restoreDocument = async (documentId: string) => {
        if (window.confirm(formatMessage({ id: LocalizationIds.INFO_DocumentRestore }))) {
            try {
                await axios.get(`/api/Ticket/RestoreDocument`, { params: { documentId } })
                await getTicketDocuments(ticket.ticketNumber, false);

            } catch (err) {
            }
        }
    }

    let handleChangeTeamMembers = async (value: ITeamMember, index: number | null, action: 'add' | 'edit' | 'delete') => {
        if (action === 'delete') {
            if (window.confirm(formatMessage({ id: LocalizationIds.INFO_RemoveUserFromTeam }))) {
                return changeTeamMembers(value, index, action);
            } else
                return false;
        } else {
            return changeTeamMembers(value, index, action);
        }
    }

    let changeTeamMembers = async (value: ITeamMember, index: number | null, action: 'add' | 'edit' | 'delete') => {

        if (!value.userName || value.userName === "" || !value.startDate || !value.endDate) {
            enqueueSnackbar(formatMessage({ id: LocalizationIds.INFO_FillFields }), { variant: "warning" });
            return false;
        }

        let teamMember = {
            ...value,
            startDate: moment(value.startDate).utc(true),
            endDate: moment(value.endDate).utc(true)
        }

        let oldTeamMembers = [...teamMembers];

        if (index == null)
            teamMembers.push({ userName: teamMember.userName, startDate: teamMember.startDate, endDate: teamMember.endDate, otherTickets: [] });
        else if (action === 'edit')
            teamMembers.splice(index, 1, teamMember);
        else if (action === 'delete')
            teamMembers.splice(index, 1);

        setTeamMembers([...teamMembers]);

        try {
            // setLoadingTeamMembers(true);

            let request = {
                teamMembers: [...teamMembers],
                ticketId: ticket.ticketId
            }

            let res = await axios.post('/api/Ticket/SaveTeamMembers', request);

            if (res.data.affectedRecords === 1) {
                enqueueSnackbar(formatMessage({ id: LocalizationIds.INFO_TeamSaved }), { variant: "success" });
                await getTicketTeamMembers();
            }

            return true;

        } catch (err) {
            setTeamMembers([...oldTeamMembers]);
            return false;
        }
    }

    let addOrder = async (selectedOrder: ITicketOrder) => {
        let idx = selectedOrder.type === SearchType.Orders
            ? selectedOrders.findIndex(x => x.orderId === selectedOrder.orderId)
            : selectedOrders.findIndex(x => x.workOrderId === selectedOrder.workOrderId);

        if (idx !== -1) {
            enqueueSnackbar(formatMessage({ id: LocalizationIds.INFO_OrderAlreadySelected }), { variant: 'warning' });
            return;
        }

        selectedOrders.push(selectedOrder);
        setSelectedOrders([...selectedOrders]);

        try {
            let request = {
                ticketId: ticket.ticketId,
                orders: selectedOrders.filter(x => x.type === SearchType.Orders),
                workOrders: selectedOrders.filter(x => x.type === SearchType.WorkOrders)
            }

            await axios.post('/api/Ticket/UpdateTicketOrders', request);
            await getTicket(false);

        } catch (err) { }
    }

    let removeOrder = async (selectedOrder: ITicketOrder) => {
        if (window.confirm(formatMessage({ id: LocalizationIds.INFO_DeleteOrder }))) {

            console.log(selectedOrder.type === SearchType.Orders);

            let idx = selectedOrder.type === SearchType.Orders
                ? selectedOrders.findIndex(x => x.orderId === selectedOrder.orderId)
                : selectedOrders.findIndex(x => x.workOrderId === selectedOrder.workOrderId);

            if (idx !== -1) {
                selectedOrders.splice(idx, 1);
                setSelectedOrders([...selectedOrders]);
            }

            try {
                let request = {
                    ticketId: ticket.ticketId,
                    orders: selectedOrders.filter(x => x.type === SearchType.Orders),
                    workOrders: selectedOrders.filter(x => x.type === SearchType.WorkOrders),
                };

                await axios.post('/api/Ticket/UpdateTicketOrders', request);
                await getTicket(false)

            } catch (err) { }
        }
    }

    let saveMessage = async (values: SaveMessage) => {
        try {
            let request = {
                ...values,
                ticketId: ticket.ticketId
            }
            await axios.post('/api/TicketMessage/SaveTicketMessage', request);
            enqueueSnackbar(formatMessage({ id: LocalizationIds.INFO_MessageSaved }), { variant: 'success' });

        } catch (err) { }
        finally {
            setLoading(false);
            await getTicketMessages();
            if ((values.messageId == null || values.messageId === '') && values.newStatus !== ticket.status) {
                await getTicket(false);
            }
        }
    }

    let openEditDialogHandler = () => {
        setShowEditDialog(true);
    }

    let closeEditDialogHandler = async (reloadTicket: boolean) => {
        setShowEditDialog(false);
        if (reloadTicket === true) {
            await getTicket(false);
        }
    }

    let openCreateReporter = () => {
        setShowCreateReporter(true);
    }

    let closeReporterDialogHandler = async (refresh: boolean) => {
        setShowCreateReporter(false);
        if (refresh === true) {
            await getTicket(false);
        }
    }


    // a specific call foreach field 
    let updateTicketStatus = async (status: TicketStatus) => {

        try {
            var response = await axios.post<SaveResponse>('/api/Ticket/UpdateTicketStatus', { value: status, ticketId: ticket.ticketId });

            if (response.data.affectedRecords > 0) {

                setTicket({ ...ticket, status });
                enqueueSnackbar("Saved changes", { variant: "success" });
            }

        } catch (err) { }

    }

    let updateTicketPriority = async (priority: TicketPriority) => {

        try {
            var response = await axios.post<SaveResponse>('/api/Ticket/UpdateTicketPriority', { value: priority, ticketId: ticket.ticketId });

            if (response.data.affectedRecords > 0) {

                setTicket({ ...ticket, priority });
                enqueueSnackbar("Saved changes", { variant: "success" });
            }

        } catch (err) { }
    }

    let updateTicketCustomerCanView = async (canView: boolean) => {
        try {
            var response = await axios.post<SaveResponse>('/api/Ticket/UpdateTicketCustomerCanView', { value: canView, ticketId: ticket.ticketId });

            if (response.data.affectedRecords > 0) {
                setTicket({ ...ticket, customerCanView: canView });
                enqueueSnackbar("Saved changes", { variant: "success" });
                getTicket(false);
            }

        } catch (err) { }
    }

    let updateTicketSubCategory = async (subcategory: string) => {
        try {
            let selectedSubCategory = appService.ticketSubCategories.find(f => f.id === subcategory);
            let customerCanView = (ticket.customerCanView === true && selectedSubCategory?.canBeSelectedByCustomer === true)

            var response = await axios.post<SaveResponse>('/api/Ticket/UpdateTicketSubCategory',
                { value: subcategory, ticketId: ticket.ticketId, customerCanView });

            if (response.data.affectedRecords > 0) {

                setTicket({
                    ...ticket,
                    ticketSubCategoryId: subcategory,
                    customerCanView
                });
                enqueueSnackbar("Saved changes", { variant: "success" });
            }

        } catch (err) { }
    }

    let updateTicketAssigneeUserName = async (assignee: string) => {
        try {
            await axios.post<SaveResponse>('/api/Ticket/UpdateTicketAssignee', { value: assignee, ticketId: ticket.ticketId });

            setTicket({ ...ticket, assigneeUserName: assignee });
            enqueueSnackbar("Saved changes", { variant: "success" });
            getTicket(false)

        } catch (err) { }

    }

    let updateTicketReporterUserName = async (reporter: string) => {
        try {
            await axios.post<SaveResponse>('/api/Ticket/UpdateTicketReporter', { value: reporter, ticketId: ticket.ticketId });

            setTicket({ ...ticket, reporterUserName: reporter });
            enqueueSnackbar("Saved changes", { variant: "success" });
            getTicket(false)

        } catch (err) { }

    }

    return (
        <Provider value={{
            ticket,
            isLoading,
            isLoadingTicketDocs,
            isLoadingWatchers,
            isLoadingTicketMessages,
            selectedOrders,
            watcherUsers,
            ticketWatchers,
            documentContainers,
            filesToEdit,
            teamMembers,
            messages,
            isDownloadingDocument,
            showEditDialog,
            reporters,
            showCreateReporter,
            teamReview,
            assignableUsers,

            updateTicketCustomerCanView,
            updateTicketSubCategory,
            updateTicketAssigneeUserName,
            updateTicketReporterUserName,
            updateTicketStatus,
            updateTicketPriority,

            searchUsers,
            searchAssignees,
            searchReporters,

            addOrder,
            removeOrder,
            addWatcher,
            updateWatcher,
            removeWatcher,

            deleteDocument,
            downloadDocument,
            downloadAttachment,
            getTicketDocuments,
            handleChangeFileName,
            handleChangeFilesToEdit,
            renameDocument,
            restoreDocument,
            handleChangeTeamMembers,
            saveMessage,
            openEditDialogHandler,
            closeEditDialogHandler,
            openCreateReporter,
            closeReporterDialogHandler,
            updateGroups
        }}>
            {children}
        </Provider>
    );
}

export default TicketDetailsProvider;